import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Sparkles } from "lucide-react";
import { useCart } from "@/lib/cart";
import CartBar from "@/components/CartBar";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { SectionHeading, CourseCard } from "@/components/shared";
import { OssdPillars } from "@/components/OssdPlanner";
import { ImageBand } from "@/components/visuals";
import { DemoFrame } from "@/pages/Demo";
import { COURSES, GRADES, DISCIPLINES, UNITS } from "@/data/courses";

const COMPULSORY = [
  { credits: 4, label: "English (one per grade)" },
  { credits: 3, label: "Mathematics (at least one in Grade 11 or 12)" },
  { credits: 2, label: "Science" },
  { credits: 1, label: "Technological Education (Grade 9 or 10)", isNew: true },
  { credits: 1, label: "Canadian History" },
  { credits: 1, label: "Canadian Geography" },
  { credits: 1, label: "The Arts" },
  { credits: 1, label: "Health & Physical Education" },
  { credits: 1, label: "French as a Second Language" },
  { credits: 1, label: "STEM-related course credit", isNew: true },
  { credits: 0.5, label: "Career Studies (with financial literacy modules)" },
  { credits: 0.5, label: "Civics and Citizenship" },
];

export default function Courses() {
  const { items } = useCart();
  const [query, setQuery] = useState("");
  const [grade, setGrade] = useState("All");
  const [discipline, setDiscipline] = useState("All");
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  const filtered = useMemo(
    () =>
      COURSES.filter((c) => {
        const q = query.trim().toLowerCase();
        const matchQ = !q || c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
        const matchG = grade === "All" || c.grade === grade;
        const matchD = discipline === "All" || c.discipline === discipline;
        return matchQ && matchG && matchD;
      }),
    [query, grade, discipline]
  );

  return (
    <>
      <PageHero
        eyebrow="OSSD Courses · Grades 9–12"
        lines={[<>Everything you need for the OSSD,</>, <span className="font-serif italic font-medium text-brand">in one catalog</span>]}
        desc="74 Ontario curriculum course credits — every university prerequisite included — plus the full diploma path: thirty credits, forty community hours and the literacy requirement. Search, filter and plan your graduation here."
        testid="courses-page-heading"
      />

      <section className="container-x py-16 sm:py-24" data-testid="ossd-pillars-section">
        <SectionHeading
          eyebrow="Diploma Requirements · Grade 9, Fall 2024 or later"
          title={<>What it takes to graduate: <span className="font-serif italic font-medium text-brand">30 credits, 40 hours, <span className="whitespace-nowrap">1 test</span></span></>}
          desc="The Ontario Secondary School Diploma is the credential recognized by every university and college in Canada. These are the current requirements for students who started Grade 9 in Fall 2024 or after — and every box is tickable with the courses below."
        />
        <div className="mt-12">
          <OssdPillars />
        </div>
      </section>

      <ImageBand
        src="https://images.unsplash.com/photo-1639765766830-d829d2fe4219?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
        alt="A graduate tossing their cap into the sky"
        tag="Thirty credits to graduation"
        testid="ossd-image-band"
      />

      <section className="container-x py-16 sm:py-24" data-testid="courses-catalog">
        <SectionHeading
          eyebrow="Course Catalog"
          title={<>74 Ontario curriculum course credits, <span className="font-serif italic font-medium text-brand">taught your way</span></>}
          desc="Search by course code — MCV4U, ENG4U, SPH4U — or filter by grade and subject. Every course includes 110 guided-learning hours, teacher support and a proctored final exam."
        />

        <Reveal className="mt-12">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-testid="courses-filters">
            <div className="relative w-full max-w-md">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by code or name, e.g. MCV4U"
                data-testid="course-search-input"
                className="field !pl-11"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {["All", ...GRADES].map((g) => (
                <button
                  key={g}
                  onClick={() => setGrade(g)}
                  data-testid={`course-grade-filter-${g}`}
                  className={`chip ${grade === g ? "chip-active" : ""}`}
                >
                  {g === "All" ? "All grades" : `Grade ${g}`}
                </button>
              ))}
            </div>
            <select
              value={discipline}
              onChange={(e) => setDiscipline(e.target.value)}
              data-testid="course-discipline-filter"
              className="field !w-auto"
              aria-label="Filter by subject"
            >
              <option value="All">All subjects</option>
              {DISCIPLINES.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-6 font-mono text-xs uppercase tracking-widest text-slate-400" data-testid="courses-count">
            Showing {filtered.length} of {COURSES.length} courses
          </p>
        </Reveal>

        {filtered.length === 0 ? (
          <div className="mt-16 rounded-2xl border border-dashed border-slate-300 p-16 text-center" data-testid="courses-empty-state">
            <p className="font-display text-lg font-bold text-ink">No courses match that search</p>
            <p className="mt-2 text-sm text-slate-500">Try a different code, grade or subject — or ask us and we will find it for you.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((course, i) => (
              <CourseCard key={course.code} course={course} index={i} onOpen={setActive} />
            ))}
          </div>
        )}
      </section>

      <section className="border-y border-slate-200/70 bg-sand/50 py-16 sm:py-24" data-testid="ossd-compulsory-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="The Credit Ledger"
              title={<>17 compulsory credits, <span className="font-serif italic font-medium text-brand">13 electives</span></>}
              desc="The requirements for students who started Grade 9 in Fall 2024 or after. Electives are where you build your story — sciences, business, philosophy, computer science or the arts. We offer all of them online."
            />
            <Reveal delay={0.2}>
              <div className="mt-8 space-y-4">
                <div className="rounded-2xl border border-brand/30 bg-brandlight/70 p-6" data-testid="elearning-note">
                  <p className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.18em] text-brand">
                    <Sparkles className="h-3.5 w-3.5" /> Included automatically
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    The ministry asks for 2 online learning credits — and every course you take with EGOS is one, so this
                    requirement fills itself as you learn (the standard opt-out is available for families who want it).
                  </p>
                </div>
                <div className="flex items-start justify-between gap-6 rounded-2xl border border-ink/10 bg-ink p-7 text-ivory" data-testid="ossd-elective-note">
                  <div>
                    <p className="font-serif italic text-xl text-brandlight">Need only a few credits?</p>
                    <p className="mt-2 text-sm leading-relaxed text-ivory/70">
                      Most of our students take one to four courses with us while attending their day school. Your marks are
                      recorded on the official Ontario Student Transcript either way.
                    </p>
                  </div>
                  <img src="/logo.webp" alt="Eletech Global Ontario School crest" className="hidden h-16 w-16 flex-shrink-0 rounded-full bg-white sm:block" />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="card-line divide-y divide-slate-100 bg-white p-2 sm:p-4" data-testid="ossd-credit-table">
              {COMPULSORY.map((row, i) => (
                <Reveal key={row.label} delay={i * 0.04}>
                  <div className="flex items-center gap-5 rounded-xl px-5 py-4 transition-colors hover:bg-sand/70">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-ink font-mono text-sm font-bold text-ivory">
                      {row.credits}
                    </span>
                    <p className="text-sm font-medium text-ink">
                      {row.label}
                      {row.isNew && (
                        <span className="ml-3 rounded-full bg-brandlight px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-brand">
                          New for 2024
                        </span>
                      )}
                    </p>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.2}>
                <div className="flex items-center gap-5 rounded-xl bg-aqualight/50 px-5 py-4">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-aqua font-mono text-sm font-bold text-white">
                    13
                  </span>
                  <p className="text-sm font-semibold text-ink">Elective credits — choose any 13 from the catalog above</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="courses-demo-section">
        <SectionHeading
          eyebrow="Live Platform Demo"
          title={<>The platform, <span className="font-serif italic font-medium text-brand">live in action</span></>}
          desc="Explore a demo preview of the learning platform below. Some functionalities are restricted — everything else is right there for you to see. Your admission counselor can login and demo the platform."
        />
        <Reveal className="mt-12">
          <DemoFrame />
        </Reveal>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        {active && (
          <DialogContent className="max-w-2xl rounded-2xl border-slate-200 bg-ivory p-0 overflow-hidden" data-testid="course-detail-dialog">
            <div className="bg-ink px-8 py-8 text-ivory">
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-brand px-3 py-1 font-mono text-sm font-bold text-white">{active.code}</span>
                <span className="font-mono text-xs uppercase tracking-widest text-ivory/60">
                  Grade {active.grade} · {active.level} · {active.discipline}
                </span>
              </div>
              <DialogHeader className="mt-4 text-left">
                <DialogTitle className="font-display text-2xl font-extrabold text-ivory">{active.name}</DialogTitle>
                <DialogDescription className="text-sm leading-relaxed text-ivory/70">{active.desc}</DialogDescription>
              </DialogHeader>
            </div>
            <div className="px-8 py-8">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="field-label">Prerequisite</p>
                  <p className="text-sm font-semibold text-ink" data-testid="course-detail-prereq">{active.prereq}</p>
                </div>
                <div>
                  <p className="field-label">Tuition (per credit)</p>
                  <p className="text-sm font-semibold text-ink" data-testid="course-detail-price">
                    ${active.price.domestic} domestic · ${active.price.international} international
                  </p>
                </div>
              </div>
              <div className="mt-7">
                <p className="field-label">Course outline · {active.hours} guided-learning hours</p>
                <ul className="mt-3 space-y-2">
                  {UNITS[active.discipline].map((u, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-600" data-testid="course-detail-unit">
                      <span className="mt-0.5 font-mono text-xs font-bold text-brand">{String(i + 1).padStart(2, "0")}</span>
                      {u}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate(`/admissions?course=${active.code}`)}
                  data-testid="course-apply-btn"
                  className="btn-accent"
                >
                  Enrol in {active.code}
                </button>
                <button onClick={() => setActive(null)} data-testid="course-detail-close-btn" className="btn-ghost">
                  Keep browsing
                </button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      {items.length > 0 && <CartBar />}
    </>
  );
}
